import { useEffect, useRef } from 'react';
import {
  caretAtomicPillIndex,
  pinnedCaretPillIndex,
  schedulePlaceCaretAfterAtomic,
} from '../utils/atomicCaret';
import { ATOMIC_PILL_SELECTOR } from '../utils/atomicCaret';
import { refreshVariableHtml } from '../utils/variableUtils';

/**
 * Keep a block's variable pills showing the CURRENT context's values.
 *
 * Same self-heal pattern as doc mention titles: when the resolution changes
 * (a value edited in the campaign panel, the doc opened through a different
 * campaign) the pill display text/state/color are rewritten and committed.
 * The canonical reference is always the pill's data-var-name — the display
 * attributes are cosmetic and re-derived per context before anything shows.
 *
 * When the editable is focused (or a fresh insert pinned a pill), we re-park
 * the caret after that pill — react-contenteditable would otherwise dump it
 * at the end of the block (painful in code blocks).
 */
export const useVariableRefresh = ({
  block,
  contentRef,
  setContent,
  onUpdate,
  resolveVariable,
  contentEditableRef = null,
}) => {
  const prevBlockContentRef = useRef(block.content);

  useEffect(() => {
    const blockContent = block.content || '';
    const blockChanged = blockContent !== (prevBlockContentRef.current || '');
    prevBlockContentRef.current = blockContent;

    // Prefer tree content when it moved (rename / undo / merge). Otherwise
    // keep contentRef so in-flight coalesced keystrokes aren't clobbered
    // when only the resolver identity changes.
    const source = blockChanged ? blockContent : contentRef.current;
    const refreshed = refreshVariableHtml(source, resolveVariable);

    if (refreshed === contentRef.current && refreshed === blockContent) return;

    const el = contentEditableRef?.current;
    let pillIndex = -1;
    if (el) {
      pillIndex = pinnedCaretPillIndex(el);
      if (pillIndex < 0 && document.activeElement === el) {
        pillIndex = caretAtomicPillIndex(el);
      }
    }

    contentRef.current = refreshed;
    setContent(refreshed);
    if (refreshed !== blockContent) {
      onUpdate(block.id, { content: refreshed });
    }

    if (pillIndex >= 0 && contentEditableRef) {
      schedulePlaceCaretAfterAtomic(
        () => contentEditableRef.current,
        (host) => host?.querySelectorAll(ATOMIC_PILL_SELECTOR)[pillIndex]
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolveVariable, block.content, block.id]);
};
